import { z } from 'zod';
import { IsoTimestamp, QuizId, SchoolId, SessionId, UserId } from './common.js';
/**
 * AIMSIS-20724 — disclosed camera capture.
 *
 * The student's camera stays live for the whole attempt on a quiz whose policy
 * is `optional` or `required`, and a proctor may take a timestamped still from
 * that live stream. Every still is its own immutable record: capturing a student
 * five times yields five reviewable images, never an overwrite.
 *
 * Boundary (see `docs/camera-policy.md` v0.2): still images only, human review
 * only, no recording, no biometric value, no automated verdict. A missing or
 * failed frame can never change a score, a status, or the remaining time.
 */
/** Policy cadence floor: no tenant may capture more often than this. */
export const CAMERA_CAPTURE_CADENCE_FLOOR_SEC = 60;
/** Hard cap on a single still, matching the remote-unlock evidence bound. */
export const CAMERA_CAPTURE_MAX_BYTES = 2 * 1024 * 1024;
/**
 * Dimension bounds. The floor rejects a frame too small to review (a 1×1 tile
 * would otherwise satisfy every other check); the ceiling keeps a full-sensor
 * upload from becoming an unbounded decode cost.
 */
export const CAMERA_CAPTURE_MIN_DIMENSION = 120;
export const CAMERA_CAPTURE_MAX_DIMENSION = 4096;
/** How long a signed upload URL stays valid. */
export const CAMERA_CAPTURE_UPLOAD_TTL_MS = 2 * 60 * 1000;
/** How long a request may stay unanswered before it is finalised as failed. */
export const CAMERA_CAPTURE_REQUEST_TTL_MS = 30 * 1000;
/** Signed read URL lifetime for the reviewer, matching unlock evidence. */
export const CAMERA_CAPTURE_READ_TTL_MS = 5 * 60 * 1000;
/**
 * Why a capture was refused. Each reason has its own user-facing message
 * (`cameraCaptureRefusalMessage`) so the proctor button is disabled WITH a
 * reason and the server refuses the same cases when the UI is bypassed.
 *
 * `request_expired`, `invalid_image` and `storage_failed` are failure reasons
 * rather than pre-flight refusals: they still land in the audit trail.
 */
export const CameraCaptureRefusalReason = z.enum([
    // Camera policy / platform gates
    'policy_disabled',
    'capture_not_enabled',
    'tenant_not_allowed',
    'kill_switch_engaged',
    'age_rule_missing',
    // Student state gates
    'consent_missing',
    'consent_declined',
    'consent_withdrawn',
    'camera_not_active',
    'session_not_active',
    // Rate gates
    'cadence',
    'quota',
    // Capture-cycle failures
    'request_expired',
    'invalid_image',
    'storage_failed',
]);
/**
 * The reviewer-facing message for a refusal.
 *
 * Shared by the API and the proctor UI so the disabled button and the server
 * refusal cannot drift into two different explanations of the same gate.
 * `cadence` is interpolated because the wait is a measured number.
 */
export function cameraCaptureRefusalMessage(reason, retryAfterSec) {
    switch (reason) {
        case 'policy_disabled':
            return 'Camera capture is disabled for this exam: the camera policy is set to Disabled';
        case 'capture_not_enabled':
            return 'Camera capture is not enabled for this exam';
        case 'tenant_not_allowed':
            return 'Camera capture is not enabled for this school';
        case 'kill_switch_engaged':
            return "Camera capture is stopped by this school's kill switch";
        case 'age_rule_missing':
            return 'Camera capture is unavailable: this school has not declared its age and guardian-consent rule';
        case 'consent_missing':
            return 'This student has not given camera consent';
        case 'consent_declined':
            return 'This student declined camera consent';
        case 'consent_withdrawn':
            return 'This student withdrew camera consent';
        case 'camera_not_active':
            return "This student's camera is not reporting active";
        case 'session_not_active':
            return "This student's attempt is not in progress or paused";
        case 'cadence': {
            const wait = Math.max(1, Math.ceil(Number(retryAfterSec ?? 0)));
            return `Wait ${wait} more second${wait === 1 ? '' : 's'} before capturing this student again`;
        }
        case 'quota':
            return 'This attempt has used its camera capture allowance';
        case 'request_expired':
            return 'The capture request expired before an image arrived';
        case 'invalid_image':
            return 'The uploaded image did not pass validation';
        case 'storage_failed':
            return 'The image could not be stored';
    }
}
/** Lifecycle of one capture record. */
export const CameraCaptureState = z.enum([
    'requested', // proctor asked, student has not answered yet
    'uploading', // a signed upload URL was issued
    'stored', // image verified and stored — the only reviewable state
    'failed', // student could not produce/send a frame
    'refused', // a gate refused the request; kept for the audit trail
    'expired', // retention elapsed and the object was deleted
    'deleted', // removed by an explicit tenant deletion request
]);
/** Review disposition. A missing frame alone may never decide it. */
export const CameraCaptureDisposition = z.enum(['no_action', 'follow_up', 'escalate']);
export const CameraCaptureMimeType = z.enum(['image/jpeg', 'image/png']);
/**
 * AIMSIS-20932 — who asked for the still.
 *
 * `proctor` is a human request through the capture button; `violation` is the
 * violation route queueing a still automatically because the quiz opted in
 * with `cameraPolicy.captureOnViolation`. Pre-existing records parse as
 * `proctor`, which is what they were.
 */
export const CameraCaptureTrigger = z.enum(['proctor', 'violation']);
/**
 * The stored record. Immutable in every field a reviewer relies on: a later
 * capture writes a NEW document, so `capturedAt` never moves and an image can
 * never be replaced under the same id.
 *
 * Deliberately absent: image bytes, signed URLs, tokens, and any biometric
 * value. Only metadata that is safe to keep after the object is gone.
 */
export const CameraCaptureRecord = z.object({
    id: z.string().uuid(),
    schoolId: SchoolId,
    quizId: QuizId,
    sessionId: SessionId,
    studentId: UserId,
    studentName: z.string().max(200).nullable().default(null),
    /** Id of the request document. Equal to `id` today; kept explicit because the
     *  policy names it separately and a future retry may reuse a request id. */
    requestId: z.string().uuid(),
    /** Bulk batches group their per-student requests under one command id. */
    batchId: z.string().uuid().nullable().default(null),
    requestedAt: IsoTimestamp,
    requestedBy: UserId,
    requestedByName: z.string().max(200).nullable().default(null),
    trigger: CameraCaptureTrigger.default('proctor'),
    /** For violation-triggered stills: the auditLog event that asked for it. */
    violationEventId: z.string().max(64).nullable().default(null),
    violationType: z.string().max(64).nullable().default(null),
    state: CameraCaptureState,
    /** SERVER acceptance time — never the student's device clock. */
    capturedAt: IsoTimestamp.nullable().default(null),
    storedAt: IsoTimestamp.nullable().default(null),
    objectPath: z.string().max(512).nullable().default(null),
    byteSize: z.number().int().nonnegative().nullable().default(null),
    mimeType: CameraCaptureMimeType.nullable().default(null),
    width: z.number().int().positive().nullable().default(null),
    height: z.number().int().positive().nullable().default(null),
    failureReason: CameraCaptureRefusalReason.nullable().default(null),
    /** Computed from the quiz's cameraPolicy.retentionHours at acceptance time. */
    retentionUntil: IsoTimestamp.nullable().default(null),
    disposition: CameraCaptureDisposition.default('no_action'),
    /** Explicit, time-bounded legal hold. Blocks deletion until it elapses. */
    legalHoldUntil: IsoTimestamp.nullable().default(null),
});
/** What the reviewer receives: the record plus a short-lived read URL. */
export const CameraCaptureView = CameraCaptureRecord.extend({
    url: z.string().url().nullable(),
    urlExpiresAt: IsoTimestamp.nullable(),
}).refine(
// A URL without its expiry is a reusable URL, which the policy forbids; a
// viewer must always be able to tell when the image stops being readable.
// The route sets the two together, so this closes the case where a caller
// supplies one and not the other.
(view) => (view.url === null) === (view.urlExpiresAt === null), { message: 'a capture view URL must always carry its expiry, and a null URL a null expiry' });
// ---- Proctor request / response ---------------------------------------------
export const ProctorCameraCaptureRequest = z.object({
    sessionIds: z.array(SessionId).min(1).max(50),
});
export const CameraCaptureRequestResult = z.object({
    sessionId: SessionId,
    studentName: z.string().max(200).nullable(),
    /** `requested` means the still is on its way; `stored` is only used when a
     *  retry finds the same request already completed. */
    state: z.enum(['requested', 'stored', 'refused']),
    captureId: z.string().uuid().nullable(),
    reason: CameraCaptureRefusalReason.nullable(),
    message: z.string().max(300).nullable(),
    retryAfterSec: z.number().int().nonnegative().nullable(),
});
export const ProctorCameraCaptureResponse = z.object({
    batchId: z.string().uuid().nullable(),
    requested: z.number().int().nonnegative(),
    refused: z.number().int().nonnegative(),
    results: z.array(CameraCaptureRequestResult),
});
export const ProctorCameraCaptureListResponse = z.object({
    captures: z.array(CameraCaptureView),
    serverTimeMs: z.number().int().nonnegative(),
    /**
     * True when older rows exist beyond the returned page.
     *
     * The gallery reads `limit + 1` and drops the extra, so a busy attempt never
     * presents a capped list as a complete one. `maxCapturesPerAttempt` allows
     * 200, so the cap is reachable inside a single attempt — without this flag a
     * proctor would read "the newest 100" as "everything there is" and conclude
     * no image was ever captured.
     */
    truncated: z.boolean().default(false),
});
/** One row of "can this student be captured right now, and if not why". */
export const CameraCaptureEligibility = z.object({
    sessionId: SessionId,
    eligible: z.boolean(),
    reason: CameraCaptureRefusalReason.nullable(),
    message: z.string().max(300).nullable(),
    retryAfterSec: z.number().int().nonnegative().nullable(),
});
export const ProctorCameraCaptureEligibilityResponse = z.object({
    /** Gates that apply to the whole exam, so the bar can disable once. */
    policyReason: CameraCaptureRefusalReason.nullable(),
    policyMessage: z.string().max(300).nullable(),
    students: z.array(CameraCaptureEligibility),
});
// ---- Student capture cycle ---------------------------------------------------
/** The student declares what it is about to upload; the server verifies it. */
export const StudentCameraCaptureUploadRequest = z.object({
    mimeType: CameraCaptureMimeType,
    byteSize: z.number().int().positive().max(CAMERA_CAPTURE_MAX_BYTES),
});
export const StudentCameraCaptureUploadResponse = z.object({
    captureId: z.string().uuid(),
    uploadUrl: z.string().url(),
    method: z.literal('PUT'),
    contentType: CameraCaptureMimeType,
    expiresAt: IsoTimestamp,
});
/** Posted after the PUT; the server re-reads the object before marking stored. */
export const StudentCameraCaptureCompleteRequest = z.object({
    mimeType: CameraCaptureMimeType,
    byteSize: z.number().int().positive().max(CAMERA_CAPTURE_MAX_BYTES),
});
export const StudentCameraCaptureFailureRequest = z.object({
    reason: z.enum(['request_expired', 'invalid_image', 'storage_failed', 'camera_not_active']),
});
/**
 * Pending requests the student must answer, oldest first.
 *
 * The route applies that order in code (`requestedAt` ascending) rather than
 * with an `orderBy`, because an `in` filter plus a limit is served by the
 * automatic index and adding an orderBy would need a composite. This docblock
 * said "newest first" while the route sorted ascending — the comment was the
 * wrong half.
 */
export const StudentCameraCapturePendingResponse = z.object({
    captures: z.array(CameraCaptureRecord),
    serverTimeMs: z.number().int().nonnegative(),
});
// ---- Configuration -----------------------------------------------------------
/**
 * Platform-level allow-list. Not editable from the app and not tenant-scoped:
 * a tenant cannot add itself. A missing document allows nobody (fail closed).
 */
export const PlatformCameraCaptureConfig = z.object({
    allowedTenantIds: z.array(SchoolId).max(2000).default([]),
    /** Approved data-residency region, recorded before implementation (policy Region row). */
    region: z.string().max(64).default('unrecorded'),
});
/**
 * Tenant controls. The kill switch stops capture with no deploy; every write is
 * audited with the actor by the admin route, and effective capture still needs
 * the quiz to opt in AND the platform allow-list to name the tenant.
 */
export const CameraCaptureTenantConfig = z.object({
    enabled: z.boolean().default(false),
    killSwitchEngaged: z.boolean().default(false),
    ageRuleDeclared: z.boolean().default(false),
    region: z.string().max(64).default('unrecorded'),
    updatedAt: IsoTimestamp.nullable().default(null),
    updatedBy: UserId.nullable().default(null),
});
export const CameraCaptureTenantConfigUpdate = z.object({
    enabled: z.boolean(),
    killSwitchEngaged: z.boolean(),
    ageRuleDeclared: z.boolean(),
});
/** Append-only record of a tenant configuration change (who flipped what). */
export const CameraCaptureConfigAuditEvent = z.object({
    id: z.string().min(1).max(64),
    at: IsoTimestamp,
    actorId: UserId,
    before: CameraCaptureTenantConfig,
    after: CameraCaptureTenantConfig,
});
// ---- Export / deletion -------------------------------------------------------
export const CameraCaptureExportRequest = z.object({
    sessionIds: z.array(SessionId).min(1).max(200),
});
/**
 * Metadata-only export: no image bytes and no reusable URL. Policy Export row
 * requires deletion status and backup expiry to be reported the same way.
 */
export const CameraCaptureExportResponse = z.object({
    requestId: z.string().uuid(),
    exportedAt: IsoTimestamp,
    captures: z.array(CameraCaptureRecord),
});
export const CameraCaptureDeletionRequest = z.object({
    sessionIds: z.array(SessionId).min(1).max(200),
});
export const CameraCaptureDeletionResponse = z.object({
    requestId: z.string().uuid(),
    deleted: z.number().int().nonnegative(),
    alreadyGone: z.number().int().nonnegative(),
    heldByLegalHold: z.number().int().nonnegative(),
    /**
     * Rows that could NOT be destroyed — a tampered object path or a storage
     * error. Reported rather than dropped: the policy requires deletion status to
     * be reported, and without this count `deleted + alreadyGone +
     * heldByLegalHold` could be less than the rows scanned with nothing saying so,
     * which reads to the caller as a complete deletion.
     */
    failed: z.number().int().nonnegative().default(0),
    /** No URL, token, or object path is ever returned here. */
    backupExpiryAt: IsoTimestamp,
});
//# sourceMappingURL=camera-capture.js.map